import { useState, useCallback, useMemo } from "react";
import { FixedSizeGrid as Grid } from 'react-window';
import { useParams, useLocation } from "wouter";
import { useDropzone } from "react-dropzone";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Upload, Trash2, Star, Download, Image as ImageIcon, Info } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { toast } from "sonner";
import { ImageMetadataDialog } from "../components/ImageMetadataDialog";
import { ImagePreview } from "../components/ImagePreview";


export default function ProjectImages() {
  const { formatDate } = useDateFormat();
  const params = useParams();
  const projectId = parseInt(params.id || "0");
  const [, navigate] = useLocation();
  const { t } = useTranslation();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);

  const { data: project } = trpc.projects.getById.useQuery({ id: projectId });
  const [page, setPage] = useState(0);
  const limit = 50;
  const { data: imageData, refetch } = trpc.images.list.useQuery({ 
    projectId,
    limit,
    offset: page * limit,
  });
  const images = imageData?.images;
  const hasMore = imageData?.hasMore || false;
  const total = imageData?.total || 0;
  const deleteImageMutation = trpc.images.delete.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteDialogOpen(false);
      toast.success(t.images.imageDeleted);
    },
  });
  const setAvatarMutation = trpc.images.setAsAvatar.useMutation({
    onSuccess: () => {
      refetch();
      toast.success(t.images.avatarSet);
    },
  });

  const uploadImageMutation = trpc.images.upload.useMutation();
  const { data: storageUsage } = trpc.settings.getStorageUsage.useQuery();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Check storage limit before upload
    if (storageUsage && storageUsage.isOverLimit) {
      toast.error(t.settings?.storageOverLimit || "Speicherlimit überschritten! Bitte erhöhen Sie das Limit in den Einstellungen oder löschen Sie Dateien.");
      return;
    }
    
    // Check if upload would exceed limit
    if (storageUsage) {
      const totalUploadSizeGB = acceptedFiles.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024 * 1024);
      const projectedUsage = storageUsage.totalGB + totalUploadSizeGB;
      
      if (projectedUsage > storageUsage.limitGB) {
        toast.error(`Upload würde Speicherlimit überschreiten (${projectedUsage.toFixed(2)} GB / ${storageUsage.limitGB} GB). Bitte erhöhen Sie das Limit oder löschen Sie Dateien.`);
        return;
      }
    }
    
    setUploading(true);
    setUploadProgress(0);

    try {
      let completed = 0;
      const total = acceptedFiles.length;
      const batchSize = 3; // Upload 3 images in parallel for better performance
      
      // Process files in batches
      for (let i = 0; i < total; i += batchSize) {
        const batch = acceptedFiles.slice(i, i + batchSize);
        
        // Upload batch in parallel
        await Promise.all(
          batch.map(async (file) => {
            // Convert file to base64
            const base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                // Remove data:image/...;base64, prefix
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            // Upload via tRPC
            await uploadImageMutation.mutateAsync({
              projectId,
              filename: file.name,
              base64Data,
              mimeType: file.type || 'image/jpeg',
            });
            
            completed++;
            setUploadProgress((completed / total) * 100);
          })
        );
      }

      toast.success(t.images.imagesUploaded);
      
      // Wait a moment for thumbnails to be fully available in storage
      // before refetching the image list
      await new Promise(resolve => setTimeout(resolve, 1500));
      refetch();
    } catch (error) {
      toast.error(t.images.uploadFailed);
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [projectId, refetch, t, uploadImageMutation, storageUsage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.raw', '.cr2', '.nef', '.arw', '.dng', '.orf', '.rw2']
    },
    maxSize: 200 * 1024 * 1024, // 200MB max
    disabled: uploading,
  });

  const handleDelete = () => {
    if (selectedImage) {
      deleteImageMutation.mutate({ id: selectedImage.id });
    }
  };

  const handleSetAvatar = (imageId: number) => {
    setAvatarMutation.mutate({ imageId });
  };

  if (!project) {
    return <div className="p-8">{t.common.loading}</div>;
  }

  return (
    <div className="container mx-auto p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate("/projects")}
          className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.common.back}
        </Button>
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{project.title}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 bg-white/60 dark:bg-gray-900/50 backdrop-blur-sm">
              <ImageIcon className="h-3.5 w-3.5" />
              {t.images.title}: {images?.length || 0}
            </span>
            {images?.length ? (
              <span className="text-muted-foreground hidden sm:inline">
                {new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(
                  (images.reduce((acc: number, img: any) => acc + (img.fileSize || 0), 0) / 1024 / 1024)
                )} MB total
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>{t.images.uploadImages}</CardTitle>
          <CardDescription>
            {t.images.dragAndDrop}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-900 ${
              isDragActive
                ? "border-primary ring-2 ring-primary/30"
                : "border-border hover:border-primary/60 hover:shadow-lg"
            } ${uploading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-lg">{t.images.dragAndDrop}</p>
            ) : (
              <>
                <p className="text-lg mb-2">{t.images.dragAndDrop}</p>
                <p className="text-sm text-muted-foreground">
                  JPEG, PNG, WebP, RAW (CR2, NEF, ARW, DNG, etc.) bis 200MB
                </p>
              </>
            )}
          </div>

          {uploading && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>{t.images.uploadInProgress}</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Images Grid / Virtualized */}
      {images && images.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ImageIcon className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">{t.images.noImages}</p>
            <p className="text-sm text-muted-foreground">{t.images.uploadFirstImage}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {images && total > 200 ? (
            <VirtualizedImagesGrid 
              images={images} 
              onSetAvatar={handleSetAvatar}
              onShowMeta={(img) => { setSelectedImage(img); setMetadataDialogOpen(true); }}
              onDelete={(img) => { setSelectedImage(img); setDeleteDialogOpen(true); }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images?.map((image) => (
                <Card key={image.id} className="overflow-hidden hover:shadow-xl hover:ring-2 hover:ring-purple-300/60 hover:translate-y-[-2px] transition-all">
                  <div className="aspect-square bg-muted relative group">
                    <ImagePreview 
                      imageId={image.id} 
                      type="thumbnail" 
                      alt={image.filename}
                      className="w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleSetAvatar(image.id)}>
                        <Star className={`h-4 w-4 ${image.isAvatar ? 'fill-current' : ''}`} />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => { setSelectedImage(image); setMetadataDialogOpen(true); }}>
                        <Info className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => { setSelectedImage(image); setDeleteDialogOpen(true); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {image.isAvatar && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium">Avatar</div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium truncate">{image.filename}</p>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>{(image.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                      <span>{formatDate(image.uploadedAt)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination Controls */}
      {images && images.length > 0 && (
        <div className="mt-8 flex justify-center items-center gap-4">
          <Button
            variant="outline"
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
          >
            {t.common.previous || "Vorherige"}
          </Button>
          <span className="text-sm text-muted-foreground">
            {page * limit + 1}-{Math.min((page + 1) * limit, total)} von {total}
          </span>
          <Button
            variant="outline"
            disabled={!hasMore}
            onClick={() => setPage(p => p + 1)}
          >
            {t.common.next || "Nächste"}
          </Button>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.images.deleteImage}</DialogTitle>
            <DialogDescription>{t.images.confirmDeleteImage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteImageMutation.isPending}
            >
              {deleteImageMutation.isPending ? t.common.loading : t.common.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metadata Dialog */}
      <ImageMetadataDialog
        image={selectedImage}
        open={metadataDialogOpen}
        onOpenChange={setMetadataDialogOpen}
      />
    </div>
  );
}

interface VirtualizedImagesGridProps {
  images: any[];
  onSetAvatar: (id: number) => void;
  onShowMeta: (img: any) => void;
  onDelete: (img: any) => void;
}

function VirtualizedImagesGrid({ images, onSetAvatar, onShowMeta, onDelete }: VirtualizedImagesGridProps) {
  const columnWidth = 260; // card width incl. padding/gap
  const rowHeight = 320; // approximate card height
  const gap = 24; // tailwind gap-6
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth - 64 : 1200;
  const columns = Math.max(1, Math.floor(viewportWidth / (columnWidth + gap)));
  const rowCount = Math.ceil(images.length / columns);

  const itemData = useMemo(() => ({ images, columns, onSetAvatar, onShowMeta, onDelete }), [images, columns, onSetAvatar, onShowMeta, onDelete]);

  return (
    <div style={{ width: '100%', height: 1200 }}>
      <Grid
        columnCount={columns}
        columnWidth={columnWidth}
        height={1200}
        rowCount={rowCount}
        rowHeight={rowHeight}
        width={viewportWidth}
        itemData={itemData}
      >
        {VirtualizedCell}
      </Grid>
    </div>
  );
}

// Cell renderer
const VirtualizedCell = ({ columnIndex, rowIndex, style, data }: any) => {
  const { images, columns, onSetAvatar, onShowMeta, onDelete } = data;
  const index = rowIndex * columns + columnIndex;
  if (index >= images.length) return null;
  const image = images[index];
  return (
    <div style={{ ...style, padding: 12 }}>
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="aspect-square bg-muted relative group">
          <ImagePreview imageId={image.id} type="thumbnail" alt={image.filename} className="w-full h-full" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => onSetAvatar(image.id)}>
              <Star className={`h-4 w-4 ${image.isAvatar ? 'fill-current' : ''}`} />
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onShowMeta(image)}>
              <Info className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onDelete(image)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {image.isAvatar && (
            <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium">Avatar</div>
          )}
        </div>
        <CardContent className="p-4">
          <p className="text-sm font-medium truncate">{image.filename}</p>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{(image.fileSize / 1024 / 1024).toFixed(2)} MB</span>
            <span>{new Date(image.uploadedAt).toLocaleDateString('bs')}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
